import type { ReactNode } from 'react';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, Button } from '@roger/ui-components';

/**
 * The shell the worksheet's saved-document panels share (the saved UB-04 and
 * the invoice): header, "Unsaved changes" pill, a notice slot, the loading
 * row, and the amber load-error alert with Retry. The panel owns what it
 * loads; this owns how that state is shown.
 */
export function SavedDocumentPanel({
  title,
  subtitle,
  ariaLabel,
  stale,
  notice,
  isLoading,
  loadingLabel,
  error,
  onRetry,
  children,
}: {
  title: string;
  subtitle: string;
  ariaLabel?: string;
  stale: boolean;
  /** Shown under the header: a line-count warning, or why the panel is off. */
  notice?: ReactNode;
  /** True while loading with nothing to show yet. */
  isLoading: boolean;
  loadingLabel: string;
  error: string | null;
  /** Omit when loading again cannot help (the server refused the request). */
  onRetry?: () => void;
  children?: ReactNode;
}) {
  return (
    <aside
      className='space-y-3 rounded border bg-background p-3'
      aria-label={ariaLabel}
    >
      <div className='flex items-center justify-between gap-2'>
        <div>
          <h3 className='text-sm font-semibold'>{title}</h3>
          <p className='text-xs text-muted-foreground'>{subtitle}</p>
        </div>
        {stale ? (
          <span className='whitespace-nowrap rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800'>
            Unsaved changes
          </span>
        ) : null}
      </div>

      {notice}

      {isLoading ? (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' /> {loadingLabel}
        </div>
      ) : null}

      {error ? (
        <Alert className='border-amber-200 bg-amber-50'>
          <AlertCircle className='h-4 w-4 text-amber-600' />
          <AlertDescription className='text-amber-800'>
            <div className='flex items-center justify-between gap-2'>
              <span>{error}</span>
              {onRetry ? (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={onRetry}
                >
                  <RefreshCw className='mr-1 h-3 w-3' /> Retry
                </Button>
              ) : null}
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      {children}
    </aside>
  );
}
